import React, { useState } from 'react';
import { X, MessageCircle, Send, Loader2 } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { toast } from '../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const t = {
  en: { title: 'Quick Inquiry', name: 'Name', email: 'Email', message: 'How can we help?', send: 'Send', success: 'Inquiry Sent!', successDesc: "We'll be in touch shortly.", error: 'Error', errorDesc: 'Something went wrong. Please try again.', placeholder: 'Looking for a specific car or have a question?' },
  es: { title: 'Consulta Rápida', name: 'Nombre', email: 'Correo', message: '¿Cómo podemos ayudar?', send: 'Enviar', success: '¡Consulta Enviada!', successDesc: 'Nos pondremos en contacto pronto.', error: 'Error', errorDesc: 'Algo salió mal. Por favor intenta de nuevo.', placeholder: '¿Buscas un auto específico o tienes una pregunta?' },
  pt: { title: 'Consulta Rápida', name: 'Nome', email: 'E-mail', message: 'Como podemos ajudar?', send: 'Enviar', success: 'Consulta Enviada!', successDesc: 'Entraremos em contato em breve.', error: 'Erro', errorDesc: 'Algo deu errado. Por favor tente novamente.', placeholder: 'Procurando um carro específico ou tem uma pergunta?' },
  fr: { title: 'Demande Rapide', name: 'Nom', email: 'E-mail', message: 'Comment pouvons-nous aider?', send: 'Envoyer', success: 'Demande Envoyée!', successDesc: 'Nous vous contacterons bientôt.', error: 'Erreur', errorDesc: 'Une erreur est survenue. Veuillez réessayer.', placeholder: 'Vous cherchez une voiture spécifique ou avez une question?' },
  it: { title: 'Richiesta Rapida', name: 'Nome', email: 'E-mail', message: 'Come possiamo aiutare?', send: 'Invia', success: 'Richiesta Inviata!', successDesc: 'Ti contatteremo a breve.', error: 'Errore', errorDesc: 'Qualcosa è andato storto. Per favore riprova.', placeholder: 'Cerchi un\'auto specifica o hai una domanda?' }
};

const InquiryWidget = () => {
  const { language } = useLanguage();
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const l = t[language] || t.en;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, subject: 'Quick Inquiry', phone: '' })
      });
      if (!res.ok) throw new Error();
      toast({ title: l.success, description: l.successDesc });
      setForm({ name: '', email: '', message: '' });
      setOpen(false);
    } catch {
      toast({ title: l.error, description: l.errorDesc, variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {/* Floating trigger button */}
      <button
        onClick={() => setOpen(!open)}
        data-testid="inquiry-widget-btn"
        className="floating-btn fixed bottom-24 right-6 z-50 w-14 h-14 bg-black text-white rounded-full flex items-center justify-center transition-all duration-300"
      >
        {open ? <X size={24} /> : <MessageCircle size={24} />}
      </button>

      {/* Widget panel */}
      {open && (
        <div
          data-testid="inquiry-widget-panel"
          className="fixed bottom-40 right-6 z-50 w-80 bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden"
          style={{ animation: 'slideUp 0.25s ease-out' }}
        >
          {/* Header */}
          <div className="bg-black text-white px-5 py-4">
            <h3 className="font-semibold text-lg">{l.title}</h3>
            <p className="text-gray-300 text-xs mt-0.5">Revs Vibes</p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-5 space-y-3">
            <input
              type="text"
              placeholder={l.name}
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
              data-testid="inquiry-name-input"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
            />
            <input
              type="email"
              placeholder={l.email}
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              required
              data-testid="inquiry-email-input"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
            />
            <textarea
              placeholder={l.placeholder}
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              required
              rows={3}
              data-testid="inquiry-message-input"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black resize-none"
            />
            <button
              type="submit"
              disabled={submitting}
              data-testid="inquiry-submit-btn"
              className="w-full bg-black text-white py-2.5 rounded-lg text-sm font-medium hover:bg-gray-800 transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {submitting ? <Loader2 className="animate-spin" size={16} /> : <Send size={16} />}
              {l.send}
            </button>
          </form>
        </div>
      )}

      <style>{`
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(16px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </>
  );
};

export default InquiryWidget;
